import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, AbstractControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { Observable, Subject, Subscription, buffer, debounceTime, distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs';
import { County } from 'src/app/model/county';
import { Region } from 'src/app/model/region.model';
import { School } from 'src/app/model/school.model';
import { SchoolDistrict } from 'src/app/model/schoolDistrict.model';
import { HelperService } from 'src/app/service/helper.service';
import { SchoolEntityService } from '../../service/school-entity.service';

@Component({
    selector: 'school-dropdown-two-way',
    templateUrl: './school-dropdown-two-way.component.html',
    styleUrl: './school-dropdown-two-way.component.scss'
})
export class SchoolDropdownTwoWayComponent implements OnInit {
    @Input() isMultiple: boolean = true;
    @Input() disableAutoComplete: boolean = false;
    @Input() autoRefreshOnRegionChange: boolean = false;
    @Input() autoRefreshOnCountyChange: boolean = false;
    @Input('reset') reset: boolean;
    @Input() required: boolean = false;
    @Input() disabled: boolean = false;
    @Input() filterSchoolDistricts: boolean = false;
    @Input() openSchoolsOnly: boolean = false;
    @Input('customLabel') customLabel: string;

    @Input() selectedSchools: School[] = [];
    @Output() selectedSchoolsChange = new EventEmitter<School[]>();

    @Input() selectedSchool: School;
    @Output() selectedSchoolChange = new EventEmitter<School>();
    @Output() loadingChange = new EventEmitter<boolean>();

    @Input() regions: Region[] = [];
    @Input() counties: County[] = [];
    @Input() schoolDistricts: SchoolDistrict[] = [];

    @ViewChild('filterRef') filterRef: ElementRef;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSelect) matSelect: MatSelect;

    fieldLabel: string;

    schoolForm: UntypedFormGroup;
    schoolIds: number[] = [];
    selectedSchoolIds: number[] = [];

    selectedSchoolName: string | undefined;
    filteredSchools: School[] = [];
    filteredSchoolsShow: School[] = [];

    schools: School[] = [];

    userRegion: Region = new Region();
    userRegionLoaded: boolean = false;
    userRegionSchools: School[] = [];

    emitTimeout: any;
    timeout: any;
    loaded: boolean = false;
    loaded$: Observable<boolean>;
    isLoading: boolean = false;
    changed: boolean = true;
    allowFirstLoad: boolean = false;

    subs: Subscription[] = [];

    pageIndex: number = 0;
    pageSize: number = 5;

    burstMS: number = 1000;
    private changes$ = new Subject<any | null>();
    private destroy$ = new Subject<void>();
    private lastEmitted: any = undefined;
    private hasInitialized = false;
    private firstLoadTimer: any = null;

    private schoolLoad$ = new Subject<any>();
    private reload$ = new Subject<void>();

    constructor(
        private fb: UntypedFormBuilder,
        private schoolService: SchoolEntityService,
        private helperService: HelperService
    ) {
        this.schoolForm = this.fb.group({
            schoolSelect: [''],
            filter: ['']
        });

        this.schoolService.setLoaded(true);
        this.loaded$ = schoolService.loaded$;
        let loadedSub = this.loaded$?.subscribe((loaded) => {
            if (!loaded) {
                this.schoolSelect.disable();
            } else {
                this.loaded = true;

                if (this.disabled) {
                    this.schoolSelect.disable();
                } else {
                    this.schoolSelect.enable();
                }
            }
        });
        this.subs.push(loadedSub);

        const burstEnd$ = this.changes$.pipe(debounceTime(this.burstMS));

        // this.changes$.pipe(buffer(burstEnd$), takeUntil(this.destroy$)).subscribe((values) => {
        //     if (!values.length) return;

        //     //console.warn('[SchoolInput] Burst buffer contents:', values);

        //     const preferred = [...values].reverse().find((v) => this.isNonEmpty(v)) ?? values[values.length - 1];

        //     //console.warn('[SchoolInput] Chosen value:', preferred);

        //     this.emitNormalized(preferred);
        // });

        this.changes$.pipe(buffer(burstEnd$), takeUntil(this.destroy$)).subscribe((events) => {
            const list = Array.isArray(events) ? events : [events];

            let latestFilters: any = null;
            let latestSelection: any = null;
            let shouldReset = false;

            for (const e of list) {
                if (e?.type === 'filters') latestFilters = e;
                if (e?.type === 'selection') latestSelection = e.value;
                if (e?.type === 'reset') shouldReset = true;
            }

            if (shouldReset) {
                this.deSelectAll();
            }

            if (latestFilters) {
                this.selectedSchoolIds = [];
                this.schoolSelect.patchValue([]);

                this.changed = true;
                this.refreshDataRequest();
            }

            if (latestSelection !== null) {
                this.emitNormalized(latestSelection);
            }
        });
    }

    private isNonEmpty(value: any): boolean {
        if (value == null) return false;
        if (Array.isArray(value)) return value.length > 0;
        return true;
    }

    private emitNormalized(value: any) {
        let normalized: number[];

        if (Array.isArray(value)) {
            normalized = value.map((r: any) => r.ID);
        } else if (value && typeof value === 'object') {
            normalized = [value.ID];
        } else if (value != null) {
            normalized = [value];
        } else {
            normalized = [];
        }

        if (this.isSame(normalized, this.lastEmitted)) return;

        this.lastEmitted = normalized;
        this.emit(normalized);
    }

    private isSame(a: any, b: any): boolean {
        if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) return false;
            return a.every((v, i) => v === b[i]);
        }

        return a === b;
    }

    subscribers() {
        let filterInputSub = this.filterInput!.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe((value: string) => {
            if (value.length === 0) {
                this.filteredSchools = this.schools;
                //this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });
            } else {
                this.filteredSchools = this.schools.filter((school) => school.NAME.toLowerCase().includes(value.toLowerCase()));
                this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });
            }
        });
        this.subs.push(filterInputSub);
    }

    emitSelect(schoolIds: number[] | number) {
        const ids = Array.isArray(schoolIds) ? schoolIds : [schoolIds];

        // block empty before first real load
        if (!this.hasInitialized && ids.length === 0) {
            return;
        }

        // data not ready yet
        if (!this.schools.length) {
            this.selectedSchoolIds = ids;
            return;
        }

        // replace, not merge
        this.selectedSchoolIds = [...ids];

        const output = this.schools.filter((s) => ids.includes(s.ID!));

        this.selectedSchoolName = output[0]?.NAME;

        this.filterInput?.patchValue('');

        if (output.length) {
            this.goToPage(output[0].ID!);
        } else {
            this.goToPage(0);
        }
        this.selectedSchoolsChange.emit(output);

        // allow empty AFTER init
        this.selectedSchoolChange.emit(output.length ? output[0] : undefined);
    }

    ngOnInit(): void {
        if (this.customLabel) {
            this.fieldLabel = this.customLabel;
        } else {
            this.fieldLabel = 'School';
        }

        this.subscribers();

        this.schoolLoad$
            .pipe(
                takeUntil(this.destroy$),
                filter((params) => this.hasValidParams(params)),
                switchMap((params) => {
                    this.isLoading = true;
                    this.loadingChange.emit(true);
                    this.schoolSelect.disable();
                    this.schoolService.setLoaded(false);

                    // switchMap ensures only the latest request is kept
                    return this.schoolService.getWithQuery(params);
                })
            )
            .subscribe({
                next: (res) => {
                    this.schools = res.sort((a, b) => a.NAME.localeCompare(b.NAME));
                    this.filteredSchools = this.schools;
                    this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });

                    this.schoolService.setLoaded(true);
                    this.isLoading = false;
                    this.loadingChange.emit(false);

                    if (!this.hasInitialized) {
                        this.hasInitialized = true;
                    }

                    if (this.schools.length === 1 && !this.disableAutoComplete) {
                        this.schoolSelect.patchValue([]);
                        this.selectAll();
                        this.emitSelect(this.selectedSchoolIds);
                    } else {
                        this.select(this.selectedSchoolIds);
                        this.emitSelect(this.selectedSchoolIds);
                    }
                },
                error: () => {
                    this.isLoading = false;
                    this.loadingChange.emit(false);
                    this.schoolService.setLoaded(true);
                },
                complete: () => {
                    if (this.disabled) {
                        this.schoolSelect.disable();
                    } else {
                        this.schoolSelect.enable();
                    }
                }
            });

        this.reload$
            .pipe(
                debounceTime(200), // adjust (200–500ms is ideal)
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.refreshDataRequest(true);
            });
    }

    private hasValidParams(params: any): boolean {
        return (
            (params.regions && params.regions.length > 0) ||
            (params.counties && params.counties.length > 0) ||
            (params.schoolDistricts && params.schoolDistricts.length > 0)
        );
    }

    ngOnDestroy() {
        this.subs.forEach((sub) => sub.unsubscribe());
        if (this.firstLoadTimer) {
            clearTimeout(this.firstLoadTimer);
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.disabled) {
            this.schoolSelect.disable();
        } else {
            this.schoolSelect.enable();
        }

        if (changes['reset'] && changes['reset'].currentValue) {
            this.changes$.next({ type: 'reset' });
        }

        if (changes['regions']) {
            this.allowFirstLoad = false;
        }

        if (changes['regions'] || changes['counties'] || changes['schoolDistricts']) {
            this.changes$.next({
                type: 'filters',
                regions: this.regions,
                counties: this.counties,
                schoolDistricts: this.schoolDistricts
            });
        }

        if (changes['selectedSchools']) {
            this.changes$.next({
                type: 'selection',
                value: changes['selectedSchools'].currentValue
            });
        }

        if (changes['selectedSchool']) {
            this.changes$.next({
                type: 'selection',
                value: changes['selectedSchool'].currentValue
            });
        }
    }

    emit(ids: any) {
        const normalizedIds = Array.isArray(ids) ? ids : [ids];

        // preserve intent
        this.selectedSchoolIds = normalizedIds;

        if (!this.schools.length) return;

        this.select(normalizedIds);
        this.emitSelect(normalizedIds);
    }

    refreshDataRequest(forceLoad?: boolean) {
        if (this.timeout) clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
            this.loadData(true, forceLoad);
        }, 300);
    }

    loadData(open: boolean, allowFirstLoad?: boolean) {
        if (!open) {
            this.select(this.selectedSchoolIds);
            this.emitSelect(this.selectedSchoolIds);
            return;
        }

        this.filterRef?.nativeElement.focus();

        // Determine if we can allow first load
        if (!this.allowFirstLoad) {
            if (this.schoolDistricts && this.schoolDistricts.length > 0) {
                // If schoolDistricts exist → allow immediately
                this.allowFirstLoad = true;
            } else {
                // Otherwise, delay by 5 seconds
                if (!this.firstLoadTimer) {
                    this.firstLoadTimer = setTimeout(() => {
                        this.allowFirstLoad = true;
                        this.firstLoadTimer = null;
                        //this.loadData(open, allowFirstLoad); // retry load
                    }, 5000);
                }
                return; // exit early
            }
        }

        if (!this.changed || !this.allowFirstLoad) return;

        this.schools = [];
        this.changed = false;
        this.filterInput!.patchValue('');

        const workingRegions = this.removeDuplicateIDs(this.regions);
        const params: any = {
            regions: workingRegions.map((r) => r.ID),
            counties: this.counties.map((c) => c.ID),
            schoolDistricts: this.schoolDistricts.map((d) => d.ID),
            filterSchoolDistricts: this.filterSchoolDistricts,
            openSchoolsOnly: this.openSchoolsOnly
        };

        // Skip API if all 3 empty
        if (!this.hasValidParams(params)) {
            this.schools = [];
            this.filteredSchools = [];
            this.selectedSchoolIds = [];
            this.schoolSelect.patchValue([]);
            this.loadingChange.emit(false);
            return;
        }

        // Trigger API
        this.schoolLoad$.next(params);
    }

    removeDuplicateIDs(arr: Array<any>): Array<any> {
        arr = arr.filter((value, index, self) => index === self.findIndex((t) => t.place === value.place && t.ID === value.ID));

        return arr;
    }

    select(selection: any) {
        this.filteredSchools = this.schools;

        if (this.isMultiple) {
            const currentPageIds = this.filteredSchoolsShow.map((s) => s.ID);

            this.selectedSchoolIds = this.selectedSchoolIds.filter((id) => !currentPageIds.includes(id));

            this.selectedSchoolIds = Array.from(new Set([...this.selectedSchoolIds, ...selection]));

            const validIds = this.filteredSchools.map((s) => s.ID);

            this.selectedSchoolIds = this.selectedSchoolIds.filter((id) => validIds.includes(id));

            this.schoolSelect.patchValue(this.selectedSchoolIds);
        } else {
            const id = Array.isArray(selection) ? selection[0] : selection;

            this.selectedSchoolIds = [id];
            this.schoolSelect.patchValue(id);

            this.goToPage(id);
        }
    }

    selectAll() {
        let selected = this.schoolSelect.value; // Currently selected

        if (this.isMultiple) {
            selected = Array.isArray(selected) ? selected : [];
            this.selectedSchoolIds = Array.from(new Set(selected.concat(this.filteredSchools.map((school) => school.ID!)))); // Select all filtered (if any filter)
            this.schoolSelect.patchValue(this.selectedSchoolIds);
        } else {
            this.selectedSchoolIds = this.filteredSchools.map((school) => school.ID!);
            this.schoolSelect.patchValue(this.selectedSchoolIds[0]);
        }
    }

    deSelectAll() {
        //this.reset = false;
        this.schoolSelect.patchValue([]);
        this.selectedSchoolIds = [];
        this.filterInput?.patchValue('');
        this.emitSelect([]);
    }

    close() {
        this.matSelect.close();
    }

    get schoolSelect(): any {
        return this.schoolForm.get('schoolSelect');
    }
    get filterInput(): AbstractControl | null {
        return this.schoolForm.get('filter');
    }

    onPageChange($event: any) {
        $event.pageIndex = $event.pageIndex < 0 ? 0 : $event.pageIndex;

        this.pageIndex = $event.pageIndex;
        this.pageSize = $event.pageSize;

        this.filteredSchoolsShow = this.filteredSchools.slice($event.pageIndex * $event.pageSize, $event.pageIndex * $event.pageSize + $event.pageSize);
    }

    goToPage(id?: number) {
        if (!this.filteredSchools || this.filteredSchools.length === 0) return;

        if (!id) {
            this.paginator.pageIndex = 0;
            this.onPageChange({ pageIndex: 0, pageSize: this.pageSize });
            return;
        }

        const index = this.filteredSchools.findIndex((s) => s.ID === id);
        if (index < 0) return;

        const pageIndex = Math.floor(index / this.pageSize);
        if (this.paginator) this.paginator.pageIndex = pageIndex;
        this.onPageChange({ pageIndex, pageSize: this.pageSize });
    }
}
